import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';
import { announce } from '@theme/live-status';

const ADDED_FOR_MS = 2500;

/**
 * Shows the result on the button that was pressed, and says it through the page's live region.
 * The side cart does not open on add (theme setting `auto_open_cart_drawer` is off) and the header
 * count is usually scrolled out of view here, so without this the add was silent and read as
 * broken (reported 2026-09-22). gate:quick-add-feedback holds it.
 * @param {HTMLButtonElement} button
 * @param {'added' | 'error'} state
 * @param {string} [message]
 */
function confirm(button, state, message) {
  const { addedLabel, addedNotice, failedNotice } = button.dataset;
  announce(state === 'added' ? addedNotice || '' : message || failedNotice || '');
  if (state !== 'added' || !addedLabel) return;
  if (!button.dataset.idleHtml) button.dataset.idleHtml = button.innerHTML;
  clearTimeout(Number(button.dataset.resetTimer));
  button.dataset.state = 'added';
  button.innerHTML = `<span aria-hidden="true">&#10003;</span> ${addedLabel}`;
  button.dataset.resetTimer = String(
    setTimeout(() => {
      button.innerHTML = button.dataset.idleHtml || button.innerHTML;
      delete button.dataset.state;
    }, ADDED_FOR_MS)
  );
}

/**
 * The product page's sideways "You may also like" row (blocks/upsell-carousel.liquid).
 *
 * Loads its cards from sections/upsell-rail.liquid through the recommendations endpoint once the
 * page has loaded and the browser is idle, reveals itself only when there is at least one card, drives the
 * arrow buttons, and turns each card's plain "Add" form into the same cart update the main Add to
 * cart button makes, so the side cart opens and counts update the Horizon way. Without scripting the
 * forms still post to /cart/add.
 */
export async function quickAdd(event, host) {
  const form = event.target.closest('form[action*="/cart/add"]');
  if (!form) return;
  event.preventDefault();
  const button = form.querySelector('button');
  // A second press while Shopify is still answering the first is the same bottle added twice
  // (a double-click on "+ Add" fires two submits). gate:add-to-cart-busy holds it.
  if (button.getAttribute('aria-busy') === 'true') return;
  button.setAttribute('aria-busy', 'true');
  const formData = new FormData(form);
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((c) => c.dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const deferred = CartLinesUpdateEvent.createPromise();
  host.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(formData.get('id')), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const cfg = fetchConfig('javascript', { body: formData });
    const res = await fetch(Theme.routes.cart_add_url, { ...cfg, headers: { ...cfg.headers, Accept: 'text/html' } });
    const json = await res.json();
    if (json.status) {
      deferred.reject(new Error(json.message));
      host.dispatchEvent(new CartErrorEvent({ error: json.message || 'Add to cart failed', code: 'INVALID' }));
      confirm(button, 'error', json.description || json.message);
      return;
    }
    const cartRes = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' } });
    const cart = await cartRes.json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: host.tagName.toLowerCase(),
        sourceId: host.id || host.tagName.toLowerCase(),
        itemCount: 1,
        productId: json.product_id,
        sections: json.sections,
        didError: false,
      },
    });
    confirm(button, 'added');
  } catch (err) {
    deferred.reject(err);
    confirm(button, 'error');
    host.dispatchEvent(new CartErrorEvent({ error: err?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
  } finally {
    button.removeAttribute('aria-busy');
  }
}

/**
 * Wraps any plain /cart/add form so it adds through the Horizon cart event instead of navigating:
 * the gift box row uses it, and so do the upsell cards.
 */
class GvQuickAdd extends HTMLElement {
  /** One handler for the element's life: a fresh arrow on every connect added one more per move. */
  #onSubmit = (/** @type {SubmitEvent} */ event) => quickAdd(event, this);

  connectedCallback() {
    this.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#onSubmit);
  }
}

if (!customElements.get('gv-quick-add')) customElements.define('gv-quick-add', GvQuickAdd);

class UpsellRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.upsell-rail__track');
    // Load once the page has settled rather than on intersection: the row starts `hidden`, and an
    // observer watching a hidden element (or a container whose box changes as the buy panel
    // hydrates) was measured not to fire on the dev proxy. The request is small and idle-timed, so
    // the buy box still paints first.
    const start = () => ('requestIdleCallback' in window ? requestIdleCallback(() => this.load(), { timeout: 2500 }) : setTimeout(() => this.load(), 300));
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
    this.querySelectorAll('.upsell-rail__arrow').forEach((b) =>
      b.addEventListener('click', () => this.page(Number(b.dataset.dir)))
    );
    this.track.addEventListener('scroll', () => this.syncArrows(), { passive: true });
    // A column that changes width changes whether there is more to scroll to.
    if ('ResizeObserver' in window) new ResizeObserver(() => this.syncArrows()).observe(this.track);
  }

  async load() {
    try {
      const res = await fetch(this.dataset.url);
      if (!res.ok) return;
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const cards = doc.querySelectorAll('.upsell-rail__card');
      if (!cards.length) return;
      this.track.replaceChildren(...cards);
      this.hidden = false;
      this.syncArrows();
    } catch (err) {
      // No row is a better failure than an empty heading.
    }
  }

  page(dir) {
    const card = this.track.querySelector('.upsell-rail__card');
    if (!card) return;
    const step = card.getBoundingClientRect().width * 2;
    this.track.scrollBy({ left: dir * step, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  syncArrows() {
    const [prev, next] = this.querySelectorAll('.upsell-rail__arrow');
    if (!prev || !next) return;
    const max = this.track.scrollWidth - this.track.clientWidth - 2;
    prev.disabled = this.track.scrollLeft <= 2;
    next.disabled = this.track.scrollLeft >= max;
    // The fade on the edge with more cards behind it (GV-18), drawn by the block's CSS.
    this.toggleAttribute('data-more-start', !prev.disabled);
    this.toggleAttribute('data-more-end', !next.disabled);
  }

}

if (!customElements.get('upsell-rail')) customElements.define('upsell-rail', UpsellRail);
